// src/pages/cmdb/ApplyArtifactDialog.tsx
// Q-930 Sprint 7 — Dialog "🚀 Aplicar" para PlanDeployTab row.
//
// DEPENDENCY: este componente consume los endpoints emitidos por backend Sprint 7
//   - POST /api/cmdb/apply-tokens          (Q-928, PR QOps #31)
//   - POST /api/cmdb/apply-tokens/{id}/revoke (Q-928, PR QOps #31)
//   - GET  /api/cmdb/apply-history          (Q-929 backend, PR QOps #31)
// Sin PR #31 mergeado en main, las llamadas devuelven 404.
//
// Flujo:
//   1. POST /cmdb/apply-tokens -> recibe signed_token ed25519
//   2. Muestra el token + comando curl para ejecutar contra el agente desde
//      una maquina con Tailscale (sin Q-915 resuelto, el VPS no alcanza al
//      agente directo — el operador es el orquestador).
//   3. Auto-polls /cmdb/apply-history para refrescar la matriz cuando el
//      agente reporta resultado al hub. Cada run_id se notifica UNA sola vez
//      (notifiedRunIds ref) para evitar toast/onApplied spam (review Imp #1).
//
// NOTA arquitectonica: el browser NO hace POST direct al agente (no tiene
// agent JWT). Cuando Q-915 (VPS en tailnet) se resuelva, se agregara opcion
// "Aplicar via backend forward" que cierra el ciclo sin copy/paste manual.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, Badge, Spinner, toast, Switch, Label,
} from '@/components/ui';
import { Copy, ExternalLink, RefreshCw, Rocket, ShieldCheck } from 'lucide-react';
import { cmdbService } from '@/services/cmdbService';
import type {
  ApplyRun, ApplyTokenCreated, DeployPlanArtifact, DeployPlanResponse,
} from '@/services/cmdbService';

export interface ApplyArtifactDialogProps {
  open: boolean;
  onClose: () => void;
  plan: DeployPlanResponse;
  artifact: DeployPlanArtifact;
  /** Llamado tras cerrar para que el caller refresque el plan (recalc estado). */
  onApplied?: () => void;
}

// Markers compartidos entre el comando curl renderizado y el help text para
// evitar drift (review Important #2). El operador hace search+replace de
// estos exactos strings al pegar SQL/JWT real.
const PLACEHOLDER_CONTENT = '<<<PEGAR_AQUI_EL_CONTENIDO_DEL_SCRIPT>>>';
const PLACEHOLDER_JWT = '<<<AGENT_JWT_AQUI>>>';

// Map artifact.type -> artifactKind para el hub-apply payload.
// El agente decide plugin (prisma vs mssql_bitacora) por target_stack del
// installed system, pero el plugin necesita saber el kind para routing del
// patron de bitacora (DDL vs SP wrapper).
function artifactKindFromType(type: string): string {
  // Convencion ecosistema Quantum:
  //   'prisma_migration' -> prisma_migration
  //   'mssql_ddl' / 'ddl' -> ddl
  //   'mssql_sp' / 'sp' -> sp
  //   etc.
  const t = type.toLowerCase();
  if (t.includes('prisma') && t.includes('migration')) return 'prisma_migration';
  if (t === 'infra' || t.startsWith('infra')) return 'infra';
  if (t.includes('trigger')) return 'trigger';
  if (t.includes('view')) return 'view';
  if (t.includes('function') || t.includes('func')) return 'function';
  if (t === 'sp' || t.includes('procedure')) return 'sp';
  return 'ddl';
}

function buildCurlCommand(opts: {
  agentBaseUrl: string;
  signedToken: string;
  artifact: DeployPlanArtifact;
  project: string;
  dryRun: boolean;
  adminUser: string;
  artifactKind: string;
}): string {
  // Heredoc shell + `curl -d @file.json` para evitar hazard de single-quote
  // en SQL con strings literales (review Important #4). Los SPs y triggers
  // Quantum tienen 'mensajes literales' que rompen `-d '<json inline>'`.
  // JSON.stringify NO escapa single-quotes — son validas dentro de un string
  // JSON pero invalidan el shell argument boundary.
  const body = {
    signedToken: opts.signedToken,
    artifactId: opts.artifact.artifact_id,
    artifactKind: opts.artifactKind,
    contentText: PLACEHOLDER_CONTENT,
    checksumExpected: opts.artifact.checksum_expected,
    bd: opts.artifact.bd ?? undefined,
    dryRun: opts.dryRun,
    project: opts.project,
    adminUser: opts.adminUser,
  };
  return [
    `# 1. Reemplazar ${PLACEHOLDER_CONTENT} con el SQL del artifact (boton "Ver" del row).`,
    `# 2. Reemplazar ${PLACEHOLDER_JWT} con el agent access token (del .env del agente).`,
    `cat > /tmp/apply-body.json <<'JSON_EOF'`,
    JSON.stringify(body, null, 2),
    `JSON_EOF`,
    ``,
    `curl -X POST '${opts.agentBaseUrl}/agent/v1/hub-apply' \\`,
    `  -H 'Authorization: Bearer ${PLACEHOLDER_JWT}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -d @/tmp/apply-body.json`,
  ].join('\n');
}

export function ApplyArtifactDialog({
  open, onClose, plan, artifact, onApplied,
}: ApplyArtifactDialogProps) {
  const [dryRun, setDryRun] = useState(true);
  const [adminUser, setAdminUser] = useState('admin@quantumprosoft.com');
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<ApplyTokenCreated | null>(null);
  const [agentBaseUrl, setAgentBaseUrl] = useState('https://AGENT_HOST:8443');
  const [polling, setPolling] = useState(false);
  const [recentRuns, setRecentRuns] = useState<ApplyRun[]>([]);
  // Q-939 Sprint 8: forward via backend (POST /api/cmdb/apply-via-agent).
  // Cuando Q-915 (VPS tailscale) este resuelto, este es el flow recomendado
  // — sin copy/paste manual del curl. Por defecto OFF hasta Q-915.
  const [applyViaBackend, setApplyViaBackend] = useState(false);
  const [forwardingViaBackend, setForwardingViaBackend] = useState(false);
  // Track run IDs already notificados via toast/onApplied. Sin esto el polling
  // cada 5s dispara toast + onApplied REPETIDAMENTE mientras la primera row
  // siga teniendo status=applied/dry_run_ok (review Important #1).
  const notifiedRunIds = useRef<Set<string>>(new Set());

  const artifactKind = useMemo(() => artifactKindFromType(artifact.type), [artifact.type]);

  // Reset state cuando se abre con artifact distinto
  useEffect(() => {
    if (open) {
      setIssued(null);
      setRecentRuns([]);
      notifiedRunIds.current = new Set();
    }
  }, [open, artifact.artifact_id]);

  const handleGenerateToken = useCallback(async () => {
    if (!artifact.checksum_expected) {
      toast.error('Artifact sin checksum — no se puede emitir token (manifest invalido)');
      return;
    }
    setIssuing(true);
    try {
      const result = await cmdbService.createApplyToken({
        artifact_id: artifact.artifact_id,
        cliente_id: plan.target.cliente_id,
        empresa_id: plan.target.empresa_id ?? undefined,
        bd: plan.target.bd ?? undefined,
        scope: { artifact_kind: artifactKind, project: plan.sistema },
        dry_run: dryRun,
        ttl_seconds: 300,
      });
      setIssued(result);
      toast.success(`Token emitido (expira en 5 min). Copiá el comando y ejecutalo en tu Tailscale machine.`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Error emitiendo token: ${msg}`);
    } finally {
      setIssuing(false);
    }
  }, [artifact, plan, dryRun, artifactKind]);

  const handleRevokeToken = useCallback(async () => {
    if (!issued) return;
    try {
      await cmdbService.revokeApplyToken(issued.token_id);
      toast.success('Token revocado');
      setIssued(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Error revocando token: ${msg}`);
    }
  }, [issued]);

  const refreshHistory = useCallback(async () => {
    setPolling(true);
    try {
      const r = await cmdbService.listApplyHistory({
        cliente_id: plan.target.cliente_id,
        artifact_id: artifact.artifact_id,
        page_size: 5,
      });
      setRecentRuns(r.items);
      // Notificar UNA sola vez por run_id — protege contra spam loop si la
      // misma row sigue siendo la top en polls subsecuentes (review Imp #1).
      const top = r.items[0];
      if (
        top
        && (top.status === 'applied' || top.status === 'dry_run_ok')
        && !notifiedRunIds.current.has(top.id)
      ) {
        notifiedRunIds.current.add(top.id);
        const verb = top.status === 'applied' ? 'aplicado' : 'dry-run OK';
        toast.success(`Apply ${verb}: ${top.bitacora_id ?? 'sin bitacora_id'}`);
        onApplied?.();
      }
    } catch (e) {
      // No toast — polling silencioso
      console.warn('apply history refresh failed:', e);
    } finally {
      setPolling(false);
    }
  }, [artifact.artifact_id, plan.target.cliente_id, onApplied]);

  // Polling automatico cada 5s mientras dialog abierto Y token emitido
  useEffect(() => {
    if (!open || !issued) return;
    refreshHistory();  // immediate first call
    const interval = setInterval(refreshHistory, 5000);
    return () => clearInterval(interval);
  }, [open, issued, refreshHistory]);

  // Q-939 Sprint 8: forward apply via backend QOps en lugar de curl manual.
  // Solo activable cuando issued y applyViaBackend=true. Backend orquesta el
  // POST al agente via Tailscale (require Q-915 done para alcance real).
  const handleApplyViaBackend = useCallback(async () => {
    if (!issued || !artifact.checksum_expected) return;
    setForwardingViaBackend(true);
    try {
      const content = await cmdbService.getScriptContent(plan.sistema, artifact.artifact_id);
      const result = await cmdbService.applyViaAgent({
        signed_token: issued.signed_token,
        artifact_id: artifact.artifact_id,
        artifact_kind: artifactKind,
        content_text: content.content_text,
        checksum_expected: artifact.checksum_expected,
        project: plan.sistema,
        cliente_id: plan.target.cliente_id,
        bd: plan.target.bd ?? undefined,
        dry_run: dryRun,
        admin_user: adminUser,
      });
      if (result.forward_status === 'success') {
        toast.success(`Apply ejecutado: ${result.agent_response?.status ?? 'OK'}`);
        onApplied?.();
      } else if (result.forward_status === 'agent_rejected') {
        toast.error(`Agente rechazó: ${result.agent_response?.message ?? 'sin detalle'} (${result.agent_status_code})`);
      } else {
        toast.error(`Agente unreachable — verifica Tailscale + estado del container`);
      }
      refreshHistory();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Error forward via backend: ${msg}`);
    } finally {
      setForwardingViaBackend(false);
    }
  }, [issued, artifact, plan, artifactKind, dryRun, adminUser, onApplied, refreshHistory]);

  const handleCopyCurl = useCallback(() => {
    if (!issued) return;
    const cmd = buildCurlCommand({
      agentBaseUrl,
      signedToken: issued.signed_token,
      artifact,
      project: plan.sistema,
      dryRun,
      adminUser,
      artifactKind,
    });
    navigator.clipboard.writeText(cmd)
      .then(() => toast.success('Comando curl copiado al portapapeles'))
      .catch(() => toast.error('No se pudo copiar — copialo manualmente'));
  }, [issued, agentBaseUrl, artifact, plan.sistema, dryRun, adminUser, artifactKind]);

  const handleCopyToken = useCallback(() => {
    if (!issued) return;
    navigator.clipboard.writeText(issued.signed_token)
      .then(() => toast.success('Signed token copiado'))
      .catch(() => toast.error('No se pudo copiar'));
  }, [issued]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="apply-dialog-title">
            <Rocket className="h-5 w-5" />
            Aplicar artifact: <code className="font-mono text-sm">{artifact.artifact_id}</code>
          </DialogTitle>
        </DialogHeader>

        {/* Detalles del artifact */}
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div><strong>Sistema:</strong> {plan.sistema}</div>
            <div><strong>Cliente:</strong> {plan.target.cliente_nombre}</div>
            <div><strong>Empresa:</strong> {plan.target.empresa_nombre ?? '—'}</div>
            <div><strong>BD:</strong> {plan.target.bd ?? '—'}</div>
            <div><strong>Type:</strong> <Badge variant="outline">{artifact.type}</Badge></div>
            <div><strong>Kind plugin:</strong> <Badge variant="secondary">{artifactKind}</Badge></div>
          </div>
          <div className="font-mono text-xs break-all">
            <strong>Checksum:</strong> {artifact.checksum_expected ?? '(no disponible)'}
          </div>
        </div>

        {/* Settings: dry_run toggle + adminUser */}
        {!issued && (
          <div className="space-y-3 mt-4 p-3 border rounded">
            <div className="flex items-center gap-3">
              <Switch
                id="dry-run-toggle"
                checked={dryRun}
                onCheckedChange={setDryRun}
                data-testid="apply-dryrun-toggle"
              />
              <Label htmlFor="dry-run-toggle" className="cursor-pointer">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="h-4 w-4" />
                  Dry run primero (recomendado)
                </span>
                <span className="text-xs text-muted-foreground block">
                  El agente valida + reporta status sin escribir cambios reales.
                </span>
              </Label>
            </div>
            <div>
              <Label htmlFor="admin-user-input">Admin user (para BitacoraAct.Usuario)</Label>
              <input
                id="admin-user-input"
                className="w-full border rounded px-2 py-1 text-sm"
                value={adminUser}
                onChange={(e) => setAdminUser(e.target.value)}
                maxLength={60}
                data-testid="apply-admin-user-input"
              />
            </div>
            <div>
              <Label htmlFor="agent-baseurl-input">URL del agente (tu Tailscale IP)</Label>
              <input
                id="agent-baseurl-input"
                className="w-full border rounded px-2 py-1 text-sm font-mono"
                value={agentBaseUrl}
                onChange={(e) => setAgentBaseUrl(e.target.value)}
                placeholder="https://100.74.153.11:8443"
                data-testid="apply-agent-url-input"
              />
            </div>
            {/* Q-939 Sprint 8: toggle forward via backend QOps */}
            <div className="flex items-center gap-3 p-2 border rounded bg-blue-50 dark:bg-blue-950">
              <Switch
                id="apply-via-backend-switch"
                checked={applyViaBackend}
                onCheckedChange={setApplyViaBackend}
                data-testid="apply-via-backend-switch"
              />
              <Label htmlFor="apply-via-backend-switch" className="cursor-pointer flex-1">
                <span className="font-semibold">Aplicar via backend (recomendado si VPS en Tailscale)</span>
                <p className="text-xs text-muted-foreground mt-1">
                  Backend QOps orquesta el apply vía Tailscale en lugar de copy/paste curl manual.
                  Requiere Q-915 (VPS Hostinger en tailnet). Si OFF, ver el helper curl tradicional.
                </p>
              </Label>
            </div>
          </div>
        )}

        {/* Token emitido: muestra curl + polling */}
        {issued && (
          <div className="space-y-3 mt-4">
            <div className="p-3 border rounded bg-amber-50 dark:bg-amber-950">
              <div className="flex items-center justify-between mb-2">
                <strong className="text-sm">Token emitido ({issued.dry_run ? 'DRY RUN' : 'REAL APPLY'})</strong>
                <Badge variant="outline">expira {new Date(issued.expires_at).toLocaleTimeString()}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Pegá el comando abajo en una terminal con Tailscale activo al agente. Reemplazá
                <code className="px-1 mx-1 bg-amber-100 dark:bg-amber-900 rounded">{PLACEHOLDER_JWT}</code>
                con el access token del agente y
                <code className="px-1 mx-1 bg-amber-100 dark:bg-amber-900 rounded">{PLACEHOLDER_CONTENT}</code>
                con el SQL visible via botón "Ver" del artifact. El heredoc + <code className="px-1 bg-amber-100 dark:bg-amber-900 rounded">-d @file.json</code> evita problemas con single-quotes en el SQL.
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                <strong>Q-938 Sprint 8 — AGENT_JWT extraction:</strong> el agente persiste el access_token a
                <code className="px-1 mx-1 bg-amber-100 dark:bg-amber-900 rounded">/var/lib/quantum-agent/access_token.json</code>
                (mode 0600, rota cada ~3min). Extraer con:
                <code className="block px-2 my-1 py-1 bg-amber-100 dark:bg-amber-900 rounded font-mono text-xs">
                  export AGENT_JWT="$(docker exec quantum-agent cat /var/lib/quantum-agent/access_token.json | jq -r .accessToken)"
                </code>
                Helper script con validación expiresAt: <code className="px-1 bg-amber-100 dark:bg-amber-900 rounded">tools/extract-agent-token.sh</code> en el repo qops-deploy-tools. Requiere
                <code className="px-1 mx-1 bg-amber-100 dark:bg-amber-900 rounded">AGENT_TOKEN_PERSIST_PATH</code>
                env var declarado en el agente (ver docker-compose.example.yml).
              </p>
              <pre className="text-xs bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap" data-testid="apply-curl-cmd">
                {buildCurlCommand({
                  agentBaseUrl, signedToken: issued.signed_token,
                  artifact, project: plan.sistema, dryRun, adminUser, artifactKind,
                })}
              </pre>
              <div className="flex gap-2 mt-2 flex-wrap">
                {applyViaBackend && (
                  <Button
                    size="sm"
                    variant="default"
                    onClick={handleApplyViaBackend}
                    disabled={forwardingViaBackend}
                    data-testid="apply-via-backend-btn"
                  >
                    {forwardingViaBackend ? <Spinner className="h-4 w-4 mr-1" /> : <Rocket className="h-4 w-4 mr-1" />}
                    Aplicar via backend
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={handleCopyCurl} data-testid="apply-copy-curl-btn">
                  <Copy className="h-4 w-4 mr-1" /> Copiar comando
                </Button>
                <Button size="sm" variant="ghost" onClick={handleCopyToken} data-testid="apply-copy-token-btn">
                  <Copy className="h-4 w-4 mr-1" /> Copiar solo token
                </Button>
                <Button size="sm" variant="destructive" onClick={handleRevokeToken} data-testid="apply-revoke-btn">
                  Revocar token
                </Button>
              </div>
            </div>

            {/* Polling apply-history para detectar reporte del agente */}
            <div className="p-3 border rounded">
              <div className="flex items-center justify-between mb-2">
                <strong className="text-sm flex items-center gap-1">
                  <RefreshCw className={`h-4 w-4 ${polling ? 'animate-spin' : ''}`} />
                  Polling resultado del agente (cada 5s)
                </strong>
                <Button size="sm" variant="outline" onClick={refreshHistory} disabled={polling}>
                  Refrescar
                </Button>
              </div>
              {recentRuns.length === 0 ? (
                <p className="text-xs text-muted-foreground">Esperando reporte del agente...</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="p-1">Status</th>
                      <th className="p-1">Dry run</th>
                      <th className="p-1">Bitácora</th>
                      <th className="p-1">Duracion</th>
                      <th className="p-1">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRuns.map((r) => (
                      <tr key={r.id} className="border-t" data-testid={`apply-run-${r.id}`}>
                        <td className="p-1">
                          <Badge variant={r.status === 'applied' || r.status === 'dry_run_ok' ? 'default' : 'destructive'}>
                            {r.status}
                          </Badge>
                        </td>
                        <td className="p-1">{r.dry_run ? '✓' : '✗'}</td>
                        <td className="p-1 font-mono">{r.bitacora_id ?? '—'}</td>
                        <td className="p-1">{r.duration_ms ? `${r.duration_ms}ms` : '—'}</td>
                        <td className="p-1">{r.applied_at ? new Date(r.applied_at).toLocaleTimeString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          {!issued ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button
                onClick={handleGenerateToken}
                disabled={issuing || !artifact.checksum_expected}
                data-testid="apply-generate-token-btn"
              >
                {issuing ? <Spinner /> : <><ExternalLink className="h-4 w-4 mr-1" /> Generar token</>}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>Cerrar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
